import { useState } from "react";
import { ScrollArea } from "@shared/components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { useInvoices } from '@features/invoices/hooks/useInvoices';
import { Button } from "@shared/components/ui/button";
import { Badge } from "@shared/components/ui/badge";
import { Progress } from "@shared/components/ui/progress";
import { fetchSignedNonInvoicedPackages } from "@/features/packages/services/packagesService";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ThreeDotLoader from "@shared/components/ThreeDotLoader";
import {
  Users,
  Package,
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  DollarSign,
  Phone,
  User,
} from "lucide-react";
import { INVOICE_QUERY_KEYS, INVOICE_STATUS_COLORS, INVOICE_ROUTES, INVOICE_TOAST_KEYS } from "../constants";

export default function CreateInvoice() {
  const { t } = useTranslation();
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedPackages, setSelectedPackages] = useState([]);
  const { createInvoice, createInvoiceLoading } = useInvoices();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: [INVOICE_QUERY_KEYS.PACKAGES_FOR_INVOICE],
    queryFn: fetchSignedNonInvoicedPackages,
  });

  if (isLoading) return <ThreeDotLoader />;
  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[400px] text-center space-y-4">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
          <XCircle className="w-8 h-8 text-red-500" />
        </div>
        <div className="space-y-2">
          <h3 className="text-lg font-medium text-slate-900">
            {t("common.error")}
          </h3>
          <p className="text-slate-600">
            {error.message || t("common.errorLoadingData")}
          </p>
        </div>
      </div>
    );
  }

  const users = data?.data || [];

  // Additional frontend filtering to ensure no invoiced packages appear
  const filteredUsers = users
    .map(user => ({
      ...user,
      packages: (user.packages || []).filter(pkg => 
        !pkg.invoiced && !pkg.invoice
      )
    }))
    .filter(user => (user.packages || []).length > 0); // Only show users with available packages

  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase();
    return INVOICE_STATUS_COLORS[statusLower] || INVOICE_STATUS_COLORS.cancelled;
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "delivered":
        return <CheckCircle2 className="w-3 h-3" />;
      case "pending":
        return <Clock className="w-3 h-3" />;
      case "processing":
        return <Package className="w-3 h-3" />;
      case "cancelled":
        return <XCircle className="w-3 h-3" />;
      default:
        return <Package className="w-3 h-3" />;
    }
  };

  const totalAmount = selectedUser
    ? (selectedUser.packages || [])
        .filter((pkg) => selectedPackages.includes(pkg._id))
        .reduce((sum, pkg) => sum + (pkg.totalAmount || 0), 0)
    : 0;

  const allSelected = selectedUser
    ? selectedPackages.length === (selectedUser.packages || []).length
    : false;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedPackages([]);
    } else {
      setSelectedPackages((selectedUser.packages || []).map((pkg) => pkg._id));
    }
  };

  return (
    <div className="mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-8 h-8 text-primary" />
            {t("invoices.createInvoice")}
          </h1>
          <p className="text-slate-600">
            Select a client and their packages to generate an invoice
          </p>
        </div>

        {selectedUser && selectedPackages.length > 0 && (
          <div className="text-right space-y-1">
            <div className="text-sm text-slate-600">
              {selectedPackages.length} packages selected
            </div>
            <div className="text-2xl font-bold text-primary flex items-center gap-1">
              <DollarSign className="w-5 h-5" />
              {totalAmount.toFixed(2)} {t("common.currency")}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Users List */}
        <Card className="lg:col-span-4 shadow-lg border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="w-5 h-5 text-primary" />
              {t("navigation.users")}
              <Badge variant="secondary" className="ml-auto">
                {filteredUsers.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]">
              {filteredUsers.length > 0 ? (
                <div className="space-y-2 p-4">
                  {filteredUsers.map((user) => (
                  <div
                    key={user.userId}
                    className={`group relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-md ${
                      selectedUser?.userId === user.userId
                        ? "border-primary bg-primary/5 shadow-md"
                        : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                    onClick={() => {
                      setSelectedUser(user);
                      setSelectedPackages([]);
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="space-y-2 flex-1">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-600" />
                          <h3 className="font-semibold text-slate-900 group-hover:text-primary transition-colors">
                            {user.brandName || user.userName || 'Unknown User'}
                          </h3>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Package className="w-3 h-3" />
                          {(user.packages || []).length} {t("packages.packages")}
                        </div>
                      </div>
                      {selectedUser?.userId === user.userId && (
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                      )}
                    </div>
                  </div>
                ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-[500px] text-center space-y-4">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                    <Users className="w-8 h-8 text-slate-400" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-medium text-slate-900">
                      No Clients Available
                    </h3>
                    <p className="text-slate-600 max-w-sm">
                      All packages have already been invoiced or no signed packages are available for invoicing.
                    </p>
                  </div>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Packages List */}
        <Card className="lg:col-span-8 shadow-lg border-0 bg-white/80 backdrop-blur-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Package className="w-5 h-5 text-primary" />
                {selectedUser
                  ? `${selectedUser.brandName || selectedUser.userName || 'Unknown User'} - Packages`
                  : t("invoices.selectUser")}
                {selectedUser && (
                  <Badge variant="secondary">
                    {(selectedUser.packages || []).length}
                  </Badge>
                )}
              </CardTitle>

              {selectedUser && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleSelectAll}
                    className="gap-2"
                  >
                    {allSelected ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : (
                      <Package className="w-4 h-4" />
                    )}
                    {allSelected ? "Deselect All" : "Select All"}
                  </Button>

                  <Button
                    disabled={
                      createInvoiceLoading || selectedPackages.length === 0
                    }
                    onClick={() => {
                      // Validate that we have all required data before proceeding
                      if (!selectedUser?.userId || selectedPackages.length === 0) {
                        toast.error(t("invoices.selectPackagesError"));
                        return;
                      }

                      createInvoice(
                        {
                          client: selectedUser.userId,
                          packages: selectedPackages,
                        },
                        {
                          onSuccess: () => {
                            toast.success(t("invoices.invoiceGenerated"));
                            setSelectedPackages([]);
                            setSelectedUser(null);
                            setTimeout(() => {
                              navigate("/invoices");
                            }, 500);
                          },
                          onError: (err) => {
                            toast.error(err?.message || t("common.errorOccurred"));
                          },
                        }
                      );
                    }}
                    className="gap-2 min-w-[140px]"
                  >
                    {createInvoiceLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {t("invoices.generatingInvoice")}
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4" />
                        {t("invoices.generateInvoice")}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>

            {selectedUser && selectedPackages.length > 0 && (
              <div className="mt-4">
                <Progress
                  value={
                    ((selectedUser.packages || []).length > 0 ? 
                      (selectedPackages.length / (selectedUser.packages || []).length) * 100 
                      : 0)
                  }
                  className="h-2"
                />
                <div className="flex justify-between text-xs text-slate-600 mt-1">
                  <span>
                    {selectedPackages.length} of {(selectedUser.packages || []).length}{" "}
                    packages
                  </span>
                  <span>
                    {((selectedUser.packages || []).length > 0 ?
                      (selectedPackages.length / (selectedUser.packages || []).length) * 100
                      : 0).toFixed(0)}
                    %
                  </span>
                </div>
              </div>
            )}
          </CardHeader>

          <CardContent className="p-0">
            {selectedUser && (selectedUser.packages || []).length > 0 ? (
              <ScrollArea className="h-[500px]">
                <div className="space-y-3 p-4">
                  {(selectedUser.packages || []).map((pkg) => {
                    const isSelected = selectedPackages.includes(pkg._id);
                    return (
                      <div
                        key={pkg._id}
                        className={`group p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-md ${
                          isSelected
                            ? "border-primary bg-primary/5 shadow-md"
                            : "border-slate-200 hover:border-slate-300 bg-white"
                        }`}
                        onClick={() => {
                          setSelectedPackages((prevSelected) => {
                            if (prevSelected.includes(pkg._id)) {
                              return prevSelected.filter(
                                (id) => id !== pkg._id
                              );
                            } else {
                              return [...prevSelected, pkg._id];
                            }
                          });
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="space-y-3 flex-1">
                            <div className="flex items-center gap-3">
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                isSelected 
                                  ? 'bg-primary border-primary text-white' 
                                  : 'border-gray-300 bg-white'
                              }`}>
                                {isSelected && <CheckCircle2 className="w-3 h-3" />}
                              </div>
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-slate-600" />
                                <span className="font-medium text-slate-900">
                                  {pkg.phone || 'N/A'}
                                </span>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                              <div className="flex items-center gap-2">
                                <User className="w-3 h-3 text-slate-500" />
                                <span className="text-slate-600">
                                  {pkg.customerName || 'N/A'}
                                </span>
                              </div>

                              <div className="flex items-center gap-2">
                                <Badge
                                  variant="outline"
                                  className={`gap-1 ${getStatusColor(
                                    pkg.status
                                  )}`}
                                >
                                  {getStatusIcon(pkg.status)}
                                  {t("status." + (pkg.status || 'pending'))}
                                </Badge>
                              </div>

                              <div className="flex items-center gap-2 justify-end">
                                <DollarSign className="w-3 h-3 text-slate-500" />
                                <span className="font-semibold text-slate-900">
                                  {pkg.totalAmount || 0} {t("common.currency")}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            ) : selectedUser ? (
              <div className="flex flex-col items-center justify-center h-[500px] text-center space-y-4">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                  <Package className="w-8 h-8 text-slate-400" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-medium text-slate-900">
                    No Packages Available
                  </h3>
                  <p className="text-slate-600 max-w-sm">
                    This client has no packages available for invoicing.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[500px] text-center space-y-4">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                  <Users className="w-8 h-8 text-slate-400" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-medium text-slate-900">
                    No Client Selected
                  </h3>
                  <p className="text-slate-600 max-w-sm">
                    Please select a client from the left panel to view their
                    packages and create an invoice.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Summary Bar */}
      {selectedUser && selectedPackages.length > 0 && (
        <Card className="shadow-lg border-0 bg-white/90 backdrop-blur-sm border-t-4 border-t-primary">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h3 className="text-lg font-semibold text-slate-900">
                  Invoice Summary
                </h3>
                <p className="text-slate-600">
                  {selectedPackages.length} packages selected for{" "}
                  {selectedUser.brandName || selectedUser.userName || 'Unknown User'}
                </p>
              </div>

              <div className="text-right space-y-1">
                <div className="text-sm text-slate-600">Total Amount</div>
                <div className="text-3xl font-bold text-primary flex items-center gap-1">
                  <DollarSign className="w-6 h-6" />
                  {totalAmount.toFixed(2)} {t("common.currency")}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
